import { io, Socket as SocketIOSocket } from 'socket.io-client';

export interface ISocket {
    id?: string;
    connected: boolean;
    on(event: string, callback: (...args: any[]) => void): void;
    off(event: string, callback?: (...args: any[]) => void): void;
    emit(event: string, data?: any): void;
    disconnect(): void;
    connect(): void;
}

function normalizeSocketUrl(url: string): string {
    if (!url) return url;
    const trimmedUrl = url.trim();
    // Local dev usually runs Socket.IO over plain ws/http; wss://localhost fails without TLS cert setup.
    if (/^wss:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(trimmedUrl)) {
        return trimmedUrl.replace(/^wss:\/\//i, 'ws://');
    }
    return trimmedUrl;
}

export class SocketIOAdapter implements ISocket {
    private socket: SocketIOSocket;

    constructor(url: string, options?: any) {
        this.socket = io(url, options);
    }

    get id() { return this.socket.id; }
    get connected() { return this.socket.connected; }

    on(event: string, callback: (...args: any[]) => void): void {
        this.socket.on(event, callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        this.socket.off(event, callback);
    }

    emit(event: string, data?: any): void {
        this.socket.emit(event, data);
    }

    disconnect(): void {
        this.socket.disconnect();
    }

    connect(): void {
        this.socket.connect();
    }
}

export class ServerlessWebSocketAdapter implements ISocket {
    private ws: WebSocket | null = null;
    private url: string;
    private eventListeners: Map<string, Set<Function>> = new Map();
    private pendingEmits: { event: string, data: any }[] = [];
    public id?: string;
    public connected: boolean = false;

    constructor(url: string) {
        this.url = url;
        this.connect();
    }

    connect(): void {
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
            this.connected = true;
            // AWS WebSocket ID is usually sent or can be retrieved, but here we can't easily get it immediately unless we implement an echo.
            // We will rely on 'connect' event simulation.
            this._trigger('connect', {});

            // Flush pending emits
            while (this.pendingEmits.length > 0) {
                const { event, data } = this.pendingEmits.shift()!;
                this.emit(event, data);
            }
        };

        this.ws.onclose = () => {
            this.connected = false;
            this._trigger('disconnect', 'closed');
        };

        this.ws.onerror = (err) => {
            this._trigger('connect_error', err);
        };

        this.ws.onmessage = (msg) => {
            try {
                const parsed = JSON.parse(msg.data);
                // Protocol: { action: 'eventName', data: payload } or standard { type: 'eventName', ...payload }
                // Our backend wrapper sends: { action: event, data: payload }
                if (parsed.action && parsed.data) {
                    this._trigger(parsed.action, parsed.data);
                } else if (parsed.action) {
                    this._trigger(parsed.action, parsed);
                }
            } catch (e) {
            }
        };
    }

    private _trigger(event: string, ...args: any[]) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach(cb => cb(...args));
        }
    }

    on(event: string, callback: (...args: any[]) => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)!.add(callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        if (!callback) {
            this.eventListeners.delete(event);
        } else {
            const listeners = this.eventListeners.get(event);
            if (listeners) {
                listeners.delete(callback);
            }
        }
    }

    emit(event: string, data?: any): void {
        if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            // If authenticating, we might want to queue it?
            // Or just standard subscribe
            if (event === 'join_room' || event === 'subscribe' || event === 'authenticate') {
                this.pendingEmits.push({ event, data });
            }
            return;
        }

        // AWS API Gateway usually expects: { action: 'routeKey', ...data }
        // Our Serverless backend expects 'subscribe' route for joining rooms.
        // For other events, we might not have backend handlers yet, but let's assume 'sendMessage' maps to 'sendmessage' route etc.
        // Actually, looking at backend implementation, we only implemented '$connect', '$disconnect', 'subscribe'.
        // Sending messages FROM client is not yet fully implemented in backend websocket.js (only 'subscribe').
        // So CLIENT can only 'subscribe'.
        // But 'authenticate' is used in ChatContext.

        // MAPPING:
        // Client 'join_room' -> Server 'subscribe' { roomId: '...' }
        // Client 'subscribe-business-account' -> Server 'subscribe' { roomId: 'business-account-XXX' }
        // Client 'subscribe-campaign' -> Server 'subscribe' { roomId: 'campaign-XXX' }
        // Client 'authenticate' -> Server 'authenticate' { userId: '...' }

        let payload = { action: event, ...data };

        if (event === 'join_room') {
            // Remap join_room to subscribe which our backend expects
            payload = { action: 'subscribe', roomId: data.roomId };
        } else if (event === 'subscribe-business-account') {
            // Map subscribe-business-account to subscribe with business-account-XXX room
            const businessAccountId = typeof data === 'string' ? data : data?.businessAccountId || data;
            payload = { action: 'subscribe', roomId: `business-account-${businessAccountId}` };
        } else if (event === 'subscribe-campaign') {
            // Map subscribe-campaign to subscribe with campaign-XXX room
            const campaignId = typeof data === 'string' ? data : data?.campaignId || data;
            payload = { action: 'subscribe', roomId: `campaign-${campaignId}` };
        } else if (event === 'unsubscribe-business-account' || event === 'unsubscribe-campaign') {
            // For unsubscribe, we could implement an unsubscribe route in the backend
            // For now, ignore these events (connections will be cleaned up on disconnect)
            return;
        }

        // If backend doesn't support the route, it falls to $default.
        this.ws.send(JSON.stringify(payload));
    }

    disconnect(): void {
        this.ws?.close();
    }
}

export function createSocket(url: string, options?: any): ISocket {
    const resolvedUrl = normalizeSocketUrl(url);

    if (resolvedUrl.startsWith('ws://') || resolvedUrl.startsWith('wss://')) {
        // Check if it looks like a standard Socket.IO URL (usually ends in /socket.io/)
        // But AWS URLs are wss://...execute-api...
        if (resolvedUrl.includes('execute-api')) {
            return new ServerlessWebSocketAdapter(resolvedUrl);
        }
    }
    return new SocketIOAdapter(resolvedUrl, options);
}
